import { useMemo, type ReactNode } from 'react'

import {
  ListFilterBar,
  type ListFilterBarSearchProps,
  type ListFilterBarToolbarSlots,
} from '@/components/filters/list-filter-bar'
import type { FilterDefinition, ListFilterId } from '@/components/filters/list-filter-types'

export type UseListFilterBarOptions<TFilters, TCtx> = {
  listId: ListFilterId
  definitions: FilterDefinition<TFilters, TCtx>[]
  filters: TFilters
  onFiltersChange: (next: TFilters) => void
  ctx: TCtx
  onClearAll: () => void
  /** Extra active state (e.g. non-default sort) included in clear-all affordance. */
  hasActiveExtras?: boolean
  search?: ListFilterBarSearchProps
  toolbar?: ListFilterBarToolbarSlots
  disabled?: boolean
}

/**
 * Shared ListFilterBar wiring — memoized `ctx`, `hasActiveFilters`, and bar element.
 * Caller owns dimension/search/sort state and `onClearAll` reset semantics.
 */
export function useListFilterBar<TFilters, TCtx>({
  listId,
  definitions,
  filters,
  onFiltersChange,
  ctx,
  onClearAll,
  hasActiveExtras = false,
  search,
  toolbar,
  disabled,
}: UseListFilterBarOptions<TFilters, TCtx>): {
  filterBar: ReactNode
  hasActiveFilters: boolean
} {
  const filterCtx = useMemo(() => ctx, [ctx])

  const hasActiveFilters = useMemo(
    () =>
      (search?.searchValue.trim().length ?? 0) > 0 ||
      hasActiveExtras ||
      definitions.some((def) => def.isNonDefault(filters, filterCtx)),
    [search?.searchValue, hasActiveExtras, definitions, filters, filterCtx],
  )

  const filterBar = (
    <ListFilterBar
      listId={listId}
      filters={filters}
      onFiltersChange={onFiltersChange}
      definitions={definitions}
      ctx={filterCtx}
      hasActiveFilters={hasActiveFilters}
      onClearAll={onClearAll}
      search={search}
      toolbar={toolbar}
      disabled={disabled}
    />
  )

  return { filterBar, hasActiveFilters }
}
